import { AfterViewInit, Component, EventEmitter, Input, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { LayerApi } from '../../../api/layer-api';
import { environment } from '../../../environment';
import { AoiService } from '../../../services/aoi.service';
import { GisToolsService } from '../../../services/gis-tools.service';
import { MapService } from '../../../services/map.service';
import { IMCMapView } from '../../../types';
import { BaseComponent } from '../../base.component';

export class IIntersectionResult {
    county: string;
    stateRoute: string;
    streetName1: string;
    streetName2: string;
    nodeId: string;
    displayName?: string;
}

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-intersection',
    templateUrl: './intersection.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './intersection.component.scss']
})
export class IntersectionComponent extends BaseComponent implements AfterViewInit {
    @Input() debounceTimer: number = 1000;
    @Input() mapViewRef: IMCMapView;

    _selectedResult: IIntersectionResult;
    @Input() set selectedResult(value: IIntersectionResult) {
        this._selectedResult = value;
        this.hasSelectionChange.emit(!!value);
    }

    get selectedResult(): IIntersectionResult {
        return this._selectedResult;
    }

    @Output() readonly hasSelectionChange = new EventEmitter<boolean>();

    error: boolean = false;
    isActive: boolean;
    results: IIntersectionResult[];
    unfilteredResults: IIntersectionResult[];
    searchText: string = '';
    searchUpdated = new Subject<string>();
    searchComplete: boolean = false;
    intersectionLayerId: number = null;
    counties: any[];

    private _county: any;
    set county(county: any) {
        this._county = county;
        this.filterByCounty();
    }
    get county() {
        return this._county;
    }

    constructor(
        _vcr: ViewContainerRef,
        private readonly aoiService: AoiService,
        private readonly layerApi: LayerApi,
        private readonly mapService: MapService,
        private readonly gisToolsService: GisToolsService
    ) {
        super(_vcr, true);
        this.searchUpdated
            .pipe(
                debounceTime(this.debounceTimer),
                distinctUntilChanged()
            )
            .subscribe(() => {
                this.search();
            });
        const environmentLayerSettings = environment.layers.filter(x => x.name === 'intersection')[0];
        this.intersectionLayerId = environmentLayerSettings.id;
    }

    loading: boolean;

    async ngAfterViewInit() {
        this.mapViewRef = this.mapViewRef ? this.mapViewRef : await this.setupMapViewRef(this.mapViewRef);
        this.mapService.mcMapView = this.mapViewRef;

        this.counties = await this.gisToolsService.getAllCounties('countyCode, name');
    }

    @Input()
    public activate = () => {
        this.hasSelectionChange.emit(!!this.selectResult);
        if (this.isActive) { return; }
        this.search();
        this.isActive = true;
    }

    @Input()
    public reset = () => {
        this.searchText = '';
        this.clearSelectedResult();
        this.clearResults();
    }

    clearSelectedResult() {
        this.selectedResult = null;
    }

    getValidStreetArrayFromSearchText() {
        if (this.searchText.length > 0) {
            const streets = this.searchText.toUpperCase().split(' AND ');
            const streetCount = 2;
            if (streets.length === streetCount && streets[0].length && streets[1].length) {
                {
                    return streets;
                }
            }
        }
        return null;
    }

    async onTextboxChange() {
        this.searchUpdated.next(this.searchText);
    }

    async search() {
        this.searchComplete = false;
        this.loading = false;
        this.error = false;
        const streets = this.getValidStreetArrayFromSearchText();
        if (streets) {
            await this.retrieveResults();
            this.searchComplete = true;
        } else {
            this.clearResults();
        }
    }

    async retrieveResults() {
        this.loading = true;
        this.selectedResult = null;
        this.results = [];
        try {
            const textForThisSearch = this.searchText;
            const streets = this.getValidStreetArrayFromSearchText();
            const fieldNames = ['cty_code,st_rt_no,street_name,street_name_ixn,node_id'];
            const query = {
                operator: 'OR',
                subqueries: [
                    {
                        operator: 'AND',
                        subqueries: [
                            { operator: 'LIKE', field: 'street_name', value: streets[0] },
                            { operator: 'LIKE', field: 'street_name_ixn', value: streets[1] }]
                    },
                    {
                        operator: 'AND',
                        subqueries: [
                            { operator: 'LIKE', field: 'street_name', value: streets[1] },
                            { operator: 'LIKE', field: 'street_name_ixn', value: streets[0] }]
                    }
                ]
            };
            const data: string[][] = await this.layerApi.getData(this.intersectionLayerId, fieldNames, query);
            if (textForThisSearch === this.searchText) {
                const countyIndex = 0, stateRouteIndex = 1, streetName1Index = 2, streetName2Index = 3, nodeIdIndex = 4;
                this.results = data.map(x => {
                    let displayName = '';
                    const nodeIdDuplicateCount = data.filter(ixn => ixn[nodeIdIndex] === x[nodeIdIndex]).length;
                    if (nodeIdDuplicateCount > 1) {
                        displayName = `${x[streetName1Index]} AND ${x[streetName2Index]} (${nodeIdDuplicateCount})`;
                    } else {
                        displayName = `${x[streetName1Index]} AND ${x[streetName2Index]}`;
                    }

                    return {
                        county: x[countyIndex],
                        stateRoute: x[stateRouteIndex],
                        streetName1: x[streetName1Index],
                        streetName2: x[streetName2Index],
                        nodeId: x[nodeIdIndex],
                        displayName
                    } as IIntersectionResult;
                }).reduce((arr, curr) => {
                    const existingIntersection = arr.find(intersection => intersection.nodeId === curr.nodeId);
                    if (existingIntersection) {
                        if (!existingIntersection.stateRoute.includes(curr.stateRoute)) {
                            // Append the missing route number
                            existingIntersection.stateRoute += `, ${curr.stateRoute}`;
                        }
                        // else leave it out as a duplicate
                    } else {
                        arr.push(curr);
                    }
                    return arr;
                }, [] as IIntersectionResult[]);
                this.unfilteredResults = this.results;
                this.loading = false;
            }
            if (this.county) {
                this.filterByCounty();
            }
        } catch {
            this.loading = false;
            this.error = true;
        }
    }

    public selectResult(result: IIntersectionResult) {
        this.selectedResult = result;
        const searchCriteria: any = {
            type: 'intersection',
            title: `${this.selectedResult.streetName1} ${this.selectedResult.streetName2}`
        };
        this.aoiService.setSearchCriterias([searchCriteria]);
    }

    clearResults() {
        this.searchComplete = false;
        this.selectedResult = null;
        this.results = [];
    }

    @Input()
    public addResultToMap = () => {
        const searchCriteria: any = {
            type: 'intersection',
            title: `${this.selectedResult.streetName1}  ${this.selectedResult.streetName2}`,
            layerId: this.intersectionLayerId,
            query: {
                operator: 'AND',
                subqueries: [
                    { field: 'node_id', operator: '=', value: this.selectedResult.nodeId },
                    { field: 'cty_code', operator: '=', value: this.selectedResult.county }
                ]
            }
        };
        this.aoiService.setSearchCriterias([searchCriteria]);
    }

    private filterByCounty() {
        if (this.unfilteredResults.length) {
            this.results = this.county !== '' ? this.unfilteredResults.filter(intersection => intersection.county === this.county.countyCode) : this.unfilteredResults;
        }
    }
}
